import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPortalStatus, PortalStatus } from '../api/portalService';

export type UsePortalStatusResult = {
  data: PortalStatus | null;
  /** Todavía no hay NADA que mostrar (primera carga). El tablero tapa todo. */
  loading: boolean;
  /**
   * Hay datos en pantalla y se están refrescando (tras confirmar, tras el
   * polling de «en proceso»). El tablero NO se tapa: quien consuma esto
   * decide dónde mostrar un spinner chico (p. ej. la fila del botón).
   */
  refreshing: boolean;
  error: Error | null;
  notFound: boolean;
  refetch: () => void;
};

/**
 * Espera entre reintentos de un REFETCH que falla (no de la primera carga).
 * Tres intentos además del primero: 1 s, 2 s, 4 s. Una escritura (confirmar,
 * aceptar la oferta) ya tuvo éxito en el servidor antes de disparar esto: el
 * refetch solo trae el estado nuevo, y un fallo acá casi siempre es
 * transitorio. Mismo espíritu que el sondeo de «en proceso» — no rendirse a
 * la primera.
 */
const REFRESH_RETRY_DELAYS_MS = [1000, 2000, 4000];

export const portalStatusQueryKey = (quoteId: string) => ['portal-status', quoteId] as const;

/**
 * Loads the portal whitelist for a quote. Skips the network when `enabled` is
 * false (mock `?state=` on the board) or when `quoteId` is empty.
 *
 * Una sola query compartida: tablero y DevPanel leen el mismo GET. Un refetch
 * no cancela el pedido en curso (`cancelRefetch: false`) para que el sondeo
 * del link de pago no tire una respuesta que ya traía `payment_url`.
 *
 * `loading` es solo «todavía no hay nada» (OUW-70). Un refetch marca
 * `refreshing` y nunca borra el último dato bueno. Reintenta solo si ya hubo
 * una respuesta (ver `REFRESH_RETRY_DELAYS_MS`).
 */
export function usePortalStatus(
  quoteId: string,
  options?: { enabled?: boolean },
): UsePortalStatusResult {
  const enabled = (options?.enabled ?? true) && Boolean(quoteId.trim());
  const queryClient = useQueryClient();
  const queryKey = portalStatusQueryKey(quoteId);

  const query = useQuery({
    queryKey,
    queryFn: () => getPortalStatus(quoteId),
    staleTime: 0,
    enabled,
    refetchOnWindowFocus: true,
    // Sin conexión, el modo por defecto («online») deja la query en `paused`
    // en vez de intentar: no hay fetch, no hay error, y el tablero se queda sin
    // nada que decidir. Acá se intenta igual y el fetch falla como siempre
    // falló: la pantalla de error con su reintento, igual que antes de React
    // Query. Y `refreshing` / `error` vuelven a ser ciertos con datos en pantalla.
    networkMode: 'always',
    // Reintenta solo si ya hubo una respuesta. Un 404 también es una
    // respuesta: la solicitud no encontrada se cachea como `null`, y con
    // `!= null` un refetch que fallara después de eso se iba derecho a la
    // pantalla de error sin ningún reintento. La misma condición que `hasData`.
    retry: (failureCount) => (
      queryClient.getQueryData(queryKey) !== undefined
      && failureCount < REFRESH_RETRY_DELAYS_MS.length
    ),
    retryDelay: (attemptIndex) => REFRESH_RETRY_DELAYS_MS[attemptIndex] ?? 4000,
  });

  const refetch = useCallback(() => {
    void queryClient.refetchQueries(
      { queryKey: portalStatusQueryKey(quoteId) },
      { cancelRefetch: false },
    );
  }, [queryClient, quoteId]);

  const hasData = query.data !== undefined;
  const error = query.isFetching
    ? null
    : query.error instanceof Error
      ? query.error
      : query.error
        ? new Error('Failed to load portal application')
        : null;

  return {
    data: query.data ?? null,
    // «Todavía no hay nada» es `isPending`, no `isLoading`. `isLoading` es
    // `isPending && isFetching`, y una query en pausa no está «fetching»:
    // daba false sin datos ni error, y el tablero caía a los valores de demo
    // de PORTAL_FIXTURE como si fueran la solicitud del cliente.
    loading: Boolean(enabled && query.isPending),
    refreshing: query.isFetching && hasData,
    error,
    notFound: query.isSuccess && query.data === null,
    refetch,
  };
}
